import { useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { FileWithStatus, FileStatus } from "./types";
import { MAX_UPLOAD_SIZE_BYTES } from "./constants";
import { isPasswordProtectedPdf } from "../../utils/pdf";

export const useFileDrop = (
  fileLists: { valid: FileWithStatus[]; invalid: FileWithStatus[] },
  setFileLists: (updater: (prev: { valid: FileWithStatus[]; invalid: FileWithStatus[] }) => { valid: FileWithStatus[]; invalid: FileWithStatus[] }) => void,
  setSnackbar: (snackbar: { open: boolean; message: string; severity?: "success" | "info" | "warning" | "error" }) => void,
  nextId: React.MutableRefObject<number>,
  jobStatuses: any,
  setJobStatuses: (updater: (prev: any) => any) => void,
  setFilesWereDeleted: (value: boolean) => void,
  setRentRollData: (data: any[]) => void,
  setVerificationResult: (result: any) => void,
  setGeneralContracts: (updater: (prev: any[]) => any[]) => void
) => {
  const fileListsRef = useRef(fileLists);
  fileListsRef.current = fileLists;

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      const newFiles = acceptedFiles.filter((f) =>
        allowedTypes.includes(f.type)
      );
      if (newFiles.length === 0) return;

      const calculateHash = async (file: File): Promise<string> => {
        const buffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
        return Array.from(new Uint8Array(hashBuffer))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
      };

      // 1. 新規ファイルの前処理 (ID付与, パスワードチェック, サイズチェック)
      const newFilesWithStatus: FileWithStatus[] = await Promise.all(
        newFiles.map(async (file) => {
          const id = `file-${nextId.current++}`;
          const isProtected = await isPasswordProtectedPdf(file);
          const isTooLarge = file.size > MAX_UPLOAD_SIZE_BYTES;
          
          let status: FileStatus = "valid";
          if (isTooLarge) {
            status = "too_large";
          } else if (isProtected) {
            status = "password_protected";
          }

          return {
            file,
            id,
            status,
          };
        })
      );

      const newValidFiles = newFilesWithStatus.filter(
        (f) => f.status === "valid"
      );
      const initiallyInvalidFiles = newFilesWithStatus.filter(
        (f) => f.status !== "valid"
      );

      let passwordProtectedCount = initiallyInvalidFiles.filter(
        (f) => f.status === "password_protected"
      ).length;
      let tooLargeCount = initiallyInvalidFiles.filter(
        (f) => f.status === "too_large"
      ).length;

      // 2. 既存の有効なファイルと新しい有効なファイルを結合してサイズでグルーピング
      const allValidFiles = [...fileListsRef.current.valid, ...newValidFiles];
      const sizeMap = new Map<number, FileWithStatus[]>();
      allValidFiles.forEach((f) => {
        const size = f.file.size;
        if (!sizeMap.has(size)) {
          sizeMap.set(size, []);
        }
        sizeMap.get(size)!.push(f);
      });

      // 3. サイズが衝突したグループのみハッシュ計算を実行
      const hashPromises: Promise<void>[] = [];
      sizeMap.forEach((files) => {
        if (files.length > 1) {
          // サイズが同じファイルが複数ある場合
          files.forEach((f) => {
            if (!f.hash) {
              // まだハッシュが計算されていないもののみ
              hashPromises.push(
                calculateHash(f.file).then((hash) => {
                  f.hash = hash;
                })
              );
            }
          });
        }
      });
      await Promise.all(hashPromises);

      // 4. 最終的な重複判定とstateの更新
      const finalValidFiles: FileWithStatus[] = [];
      const finalInvalidFiles: FileWithStatus[] = [...initiallyInvalidFiles];
      const existingHashes = new Set(
        fileListsRef.current.valid.map((f) => f.hash).filter(Boolean)
      );
      const newHashesInBatch = new Set<string>();
      let duplicateCount = 0;

      newValidFiles.forEach((newFile) => {
        const group = sizeMap.get(newFile.file.size);
        let isDuplicate = false;

        // サイズがユニークなファイルは重複しない
        if (group && group.length > 1) {
          // サイズが重複している場合、ハッシュで確認
          if (newFile.hash) {
            if (
              existingHashes.has(newFile.hash) ||
              newHashesInBatch.has(newFile.hash)
            ) {
              isDuplicate = true;
            }
          }
        }

        if (isDuplicate) {
          finalInvalidFiles.push({ ...newFile, status: "duplicate" });
          duplicateCount++;
        } else {
          finalValidFiles.push(newFile);
          if (newFile.hash) {
            newHashesInBatch.add(newFile.hash);
          }
        }
      });

      const messages = [];
      if (passwordProtectedCount > 0) {
        messages.push(
          `${passwordProtectedCount}件のパスワード付きファイルは除外されます。`
        );
      }
      if (duplicateCount > 0) {
        messages.push(`${duplicateCount}件の重複ファイルは除外されます。`);
      }
      if (tooLargeCount > 0) {
        messages.push(`${tooLargeCount}件の32MB超のファイルは除外されます。`);
      }
      if (messages.length > 0) {
        setSnackbar({ open: true, message: messages.join(" ") });
      }

      setFileLists((prev) => ({
        valid: [...prev.valid, ...finalValidFiles],
        invalid: [...prev.invalid, ...finalInvalidFiles],
      }));
    },
    [setSnackbar, setFileLists, nextId]
  );

  const handleRemoveFile = useCallback((id: string) => {
    // Find the job associated with the file ID to check its type
    const jobIdToRemove = Object.keys(jobStatuses).find(
      (jobId) => jobStatuses[jobId].fileId === id
    );
    const jobToRemove = jobIdToRemove ? jobStatuses[jobIdToRemove] : undefined;

    // Mark that a deletion occurred for the summary popup logic
    setFilesWereDeleted(true);

    // Remove from the UI list
    setFileLists((prev) => ({
      valid: prev.valid.filter((f) => f.id !== id),
      invalid: prev.invalid.filter((f) => f.id !== id),
    }));

    // If the deleted file was a rent roll, clear rent roll data and verification results.
    // The verification useEffect will re-run on generalContracts change, but since rent roll is gone,
    // it will correctly produce a null verificationResult.
    if (jobToRemove?.data?.type === "rent_roll") {
      setRentRollData([]);
      setVerificationResult(null);
    }

    // Always filter generalContracts in case the deleted file was a contract document.
    setGeneralContracts((prev) => prev.filter((c) => c.fileId !== id));

    // Remove the job from jobStatuses using the found jobId.
    if (jobIdToRemove) {
      setJobStatuses((prev) => {
        const newJobs = { ...prev };
        delete newJobs[jobIdToRemove];
        return newJobs;
      });
    }
  }, [
    jobStatuses,
    setFilesWereDeleted,
    setFileLists,
    setRentRollData,
    setVerificationResult,
    setGeneralContracts,
    setJobStatuses,
  ]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
    },
  });

  return {
    onDrop,
    getRootProps,
    getInputProps,
    isDragActive,
    handleRemoveFile,
  };
};